import { useMemo, useState, type ReactNode } from 'react';
import { HardDrive, Plus, Search, Filter, Download } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, FilterBuilder, type ColumnDef, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import { Button, Input, Badge, Skeleton, Popover, PopoverTrigger, PopoverContent } from '@vnetwork/ui/primitives';
import { RowMenu } from '../components/RowMenu';
import { RefreshButton } from '../_shared/AnalyticsKit';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useOutputStorageConfig, useDeleteOutputStorage } from '@/api/media/shared-storage';
import type { FileTransferConfig } from '@/api/media/configs';
import type { OutputStorageType, OutputStorageConfig } from '@/api/media/shared-storage-types';
import { FileTransferCreateDialog } from './file-transfer-create/FileTransferCreateDialog';
import type { EditRow } from './file-transfer-create/storage-form-types';
import styles from './FileTransferList.module.css';

// ─── types ───────────────────────────────────────────────────────────────────

interface OutputRow {
  id: string;
  originalId: number;
  name: string;
  type: string;
  url: string;
  authPrimary: string;
  authSecondary: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  raw: any;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function mapOutputRows(data?: OutputStorageConfig): OutputRow[] {
  if (!data) return [];
  const rows: OutputRow[] = [];
  (data.s3 ?? []).forEach((r) => rows.push({ id: `s3-${r.id}`, originalId: r.id, name: r.name, type: 'S3', url: `s3://${r.bucket}.s3.${r.region}.amazonaws.com`, authPrimary: 'Access Key', authSecondary: r.access_key_id, raw: r }));
  (data.ftp ?? []).forEach((r) => rows.push({ id: `ftp-${r.id}`, originalId: r.id, name: r.name, type: 'FTP', url: `ftp://${r.username}@${r.host}:${r.port}`, authPrimary: 'Username', authSecondary: r.username, raw: r }));
  (data.https ?? []).forEach((r) => rows.push({ id: `http-${r.id}`, originalId: r.id, name: r.name, type: 'HTTP', url: r.host, authPrimary: 'Auth', authSecondary: r.auth_type === 2 ? 'Basic' : 'None', raw: r }));
  (data.sftp ?? []).forEach((r) => rows.push({ id: `sftp-${r.id}`, originalId: r.id, name: r.name, type: 'SFTP', url: `sftp://${r.username}@${r.host}:${r.port}`, authPrimary: 'Username', authSecondary: r.username, raw: r }));
  (data.webdav ?? []).forEach((r) => rows.push({ id: `webdav-${r.id}`, originalId: r.id, name: r.name, type: 'WebDAV', url: r.host, authPrimary: 'Auth', authSecondary: r.auth_type === 2 ? 'Basic' : 'None', raw: r }));
  return rows;
}

// Protocol is the one real filter dimension for storage destinations.
const PROTOCOLS = ['S3', 'FTP', 'SFTP', 'HTTP', 'WebDAV'];

/** Download the given storage destinations as a CSV file. */
function exportCsv(rows: OutputRow[]) {
  const head = ['name', 'protocol', 'endpoint', 'auth', 'auth_value'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) => [r.name, r.type, r.url, r.authPrimary, r.authSecondary].map(esc).join(','))
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `storage-destinations-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── component ───────────────────────────────────────────────────────────────

export interface FileTransferListProps {
  serviceId: string;
  config: FileTransferConfig;
  /** Console-specific breadcrumb (root differs per console: Live Transcoding vs Cloud Encoding). */
  breadcrumbs?: ReactNode;
}

/** Config-driven output storage list for Cloud Encoding and Live Transcoding. */
export function FileTransferList({ serviceId, config, breadcrumbs }: FileTransferListProps) {
  const { show } = useToast();

  const { data, isLoading, refetch } = useOutputStorageConfig(serviceId, config);

  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<EditRow | undefined>();
  const [pendingDelete, setPendingDelete] = useState<OutputRow | null>(null);

  const deleteMut = useDeleteOutputStorage(serviceId, config, {
    onSuccess: () => { show({ tone: 'success', title: 'Storage deleted' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to delete storage' }),
  });

  const allRows = useMemo(() => mapOutputRows(data), [data]);

  // Active protocol scope derived from the FilterBuilder conditions (multiselect
  // values are comma-joined per row, so split + flatten across matching rows).
  const protocolFilters = useMemo(() => {
    const vals = conditions
      .filter((c) => c.field === 'type')
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
    return new Set(vals);
  }, [conditions]);

  const filterFields: FilterField[] = [
    {
      value: 'type',
      label: 'Protocol',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: PROTOCOLS.map((p) => ({ value: p, label: p })),
    },
  ];

  const rows = useMemo(() => {
    let r = allRows;
    if (protocolFilters.size) r = r.filter((x) => protocolFilters.has(x.type));
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => x.name.toLowerCase().includes(q) || x.url.toLowerCase().includes(q));
    }
    return r;
  }, [allRows, protocolFilters, search]);

  const loading = isLoading;

  const columns = useMemo<ColumnDef<OutputRow, unknown>[]>(() => [
    {
      id: 'name',
      header: 'Storage',
      cell: ({ row }) => (
        <div>
          <div className={styles.cellStorageName}>{row.original.name}</div>
          <div className={styles.cellStorageUrl}>{row.original.url || '—'}</div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Protocol',
      // Protocol is a category label, not a status — neutral/subtle so FTP/SFTP
      // aren't misread as a warning state (matches the reference table pages).
      cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.type}</Badge>,
    },
    {
      id: 'auth',
      header: 'Authentication',
      cell: ({ row }) => (
        <span className={styles.cellAuthLabel}>
          {row.original.authPrimary}: <span className={styles.cellAuthValue}>{row.original.authSecondary}</span>
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'Edit', onSelect: () => { setEditRow({ raw: row.original.raw, type: row.original.type, originalId: row.original.originalId }); setCreateOpen(true); } },
            { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
          ]}
        />
      ),
    },
  ], []);

  return (
    <>
      <Container size="xl">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={`${config.labels.pageTitle}${allRows.length ? ` (${allRows.length})` : ''}`}
          description={config.labels.pageDescription}
          actions={
            <Button
              startIcon={<Plus size={15} />}
              onClick={() => { setEditRow(undefined); setCreateOpen(true); }}
            >
              {config.labels.createButtonText}
            </Button>
          }
        />
        <Toolbar
          start={
            <Input
              placeholder="Search by name or endpoint…"
              startSlot={<Search size={15} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              wrapperClassName={styles.searchInput}
            />
          }
          end={
            <>
              <RefreshButton onRefresh={() => void refetch()} />
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{conditions.length ? ` (${conditions.length})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className={styles.filterPopoverContent}>
                  <FilterBuilder
                    fields={filterFields}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={(c) => {
                      // Drop empty rows so a blank filter doesn't count as active.
                      setConditions(c.filter((x) => x.value.trim()));
                      setFilterOpen(false);
                    }}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>
              {conditions.length > 0 && (
                <Button variant="ghost" onClick={() => setConditions([])}>
                  Clear
                </Button>
              )}
              <Button
                variant="secondary"
                startIcon={<Download size={15} />}
                disabled={rows.length === 0}
                onClick={() => exportCsv(rows)}
              >
                Export
              </Button>
            </>
          }
        />

        {loading ? (
          <div className={styles.skeletonGrid}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            emptyState={
              // First-use shows a creation hint; once destinations exist the empty
              // state means the search/type filter excluded everything.
              allRows.length === 0 ? (
                <EmptyState
                  icon={<HardDrive size={20} />}
                  title="No storage configurations"
                  description="Add a storage destination to start transferring output files."
                  actions={
                    <Button
                      startIcon={<Plus size={14} />}
                      onClick={() => { setEditRow(undefined); setCreateOpen(true); }}
                    >
                      {config.labels.createButtonText}
                    </Button>
                  }
                />
              ) : (
                'No storage destinations match your search or filters.'
              )
            }
            footer={allRows.length ? `Showing ${rows.length} of ${allRows.length}` : undefined}
          />
        )}
      </Container>

      <FileTransferCreateDialog
        open={createOpen}
        onOpenChange={(open) => { setCreateOpen(open); if (!open) setEditRow(undefined); }}
        serviceId={serviceId}
        config={config}
        editRow={editRow}
        onDone={() => setCreateOpen(false)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title={config.labels.deleteDialogTitle}
        description={config.labels.deleteDialogDescription(pendingDelete?.name ?? '')}
        confirmLabel="Delete"
        destructive
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMut.mutate({ outputStorageType: pendingDelete.type.toLowerCase() as OutputStorageType, outputStorageId: String(pendingDelete.originalId) });
        }}
      />
    </>
  );
}
